import { ItemBar, ItemBarLeft } from "components/ItemBar";
import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import { sizer } from "layout/styles/styled/sizer";
import { debounce } from "lodash";
import { Badge } from "primereact/badge";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { TabPanel, TabView } from "primereact/tabview";
import { Tooltip } from "primereact/tooltip";
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  useGetHstSyncRecordsQuery,
  useGetHstSyncStatsQuery,
} from "store/queries/hstSync";
import styled from "styled-components";
import { HstSyncStatus, IHstSyncRecord } from "types/HstSync";

const StatsContainer = styled.div`
  display: flex;
  gap: ${sizer(3)};
  flex-wrap: wrap;
  margin-bottom: ${sizer(4)};
`;

const StatCard = styled.div<{ variant?: string; clickable?: boolean }>`
  min-width: 140px;
  padding: ${sizer(3)};
  border-radius: 8px;
  text-align: center;
  border: 1px solid var(--surface-border);
  background-color: ${({ variant }) => {
    switch (variant) {
      case "pending":
        return "var(--yellow-50)";
      case "inProgress":
        return "var(--blue-50)";
      case "synced":
        return "var(--green-50)";
      case "failed":
        return "var(--red-50)";
      case "skipped":
        return "var(--surface-ground)";
      default:
        return "var(--surface-card)";
    }
  }};
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    ${({ clickable }) =>
      clickable &&
      `
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    `}
  }

  .stat-count {
    font-size: 28px;
    font-weight: 700;
    color: ${({ variant }) => {
      switch (variant) {
        case "pending":
          return "var(--yellow-700)";
        case "inProgress":
          return "var(--blue-700)";
        case "synced":
          return "var(--green-700)";
        case "failed":
          return "var(--red-700)";
        case "skipped":
          return "var(--text-color-secondary)";
        default:
          return "var(--text-color)";
      }
    }};
  }

  .stat-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-color-secondary);
    margin-top: ${sizer(1)};
  }
`;

const StyledDataTable = styled(DataTable)`
  .p-datatable-tbody > tr > td {
    padding: ${sizer(2)} ${sizer(3)};
  }
`;

const StatusBadge = styled(Badge)<{ status: HstSyncStatus }>`
  && {
    background-color: ${({ status }) => {
      switch (status) {
        case "PENDING":
          return "var(--yellow-500)";
        case "IN_PROGRESS":
          return "var(--blue-500)";
        case "SYNCED":
          return "var(--green-500)";
        case "FAILED":
          return "var(--red-500)";
        case "SKIPPED":
          return "var(--gray-500)";
        default:
          return "var(--gray-500)";
      }
    }};
  }
`;

const ErrorText = styled.span`
  color: var(--red-600);
  font-size: 12px;
  max-width: 200px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

const statusOptions = [
  { label: "All Statuses", value: null },
  { label: "Pending", value: "PENDING" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Synced", value: "SYNCED" },
  { label: "Failed", value: "FAILED" },
  { label: "Skipped", value: "SKIPPED" },
];

const PAGE_SIZE = 20;

export function HstSyncPage() {
  useDocumentTitle("HST Payment Sync");

  const [first, setFirst] = useState(0);
  const [patientNameFilter, setPatientNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<HstSyncStatus | null>(null);
  const [debouncedPatientName, setDebouncedPatientName] = useState("");

  const debouncedSetPatientName = useCallback(
    debounce((value: string) => {
      setDebouncedPatientName(value);
      setFirst(0);
    }, 400),
    []
  );

  useEffect(() => {
    debouncedSetPatientName(patientNameFilter);
    return () => {
      debouncedSetPatientName.cancel();
    };
  }, [patientNameFilter, debouncedSetPatientName]);

  const { data: statsData, isLoading: statsLoading } =
    useGetHstSyncStatsQuery();

  const { data: recordsData, isLoading: recordsLoading } =
    useGetHstSyncRecordsQuery({
      patientName: debouncedPatientName || undefined,
      status: statusFilter || undefined,
      page: first / PAGE_SIZE,
      pageSize: PAGE_SIZE,
    });

  const stats = statsData?.data;
  const records = recordsData?.data || [];
  const totalRecords = recordsData?.total || 0;

  const handleStatusCardClick = (status: HstSyncStatus | null) => {
    setStatusFilter(status);
    setFirst(0);
  };

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDob = (dateStr: string): string => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatAmount = (amountInCents: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amountInCents / 100);
  };

  const patientNameTemplate = (row: IHstSyncRecord) => {
    return (
      <span className="sensitive">
        {row.patientFirstName} {row.patientLastName}
      </span>
    );
  };

  const dobTemplate = (row: IHstSyncRecord) => {
    return <span className="sensitive">{formatDob(row.patientDob)}</span>;
  };

  const amountTemplate = (row: IHstSyncRecord) => {
    if (row.amountInCents === null || row.amountInCents === undefined) {
      return <span />;
    }
    return <span>{formatAmount(row.amountInCents)}</span>;
  };

  const clearPriceTemplate = (row: IHstSyncRecord) => {
    if (row.clearPriceInCents === null || row.clearPriceInCents === undefined) {
      return <span />;
    }
    return <span>{formatAmount(row.clearPriceInCents)}</span>;
  };

  const syncTypeTemplate = (row: IHstSyncRecord) => {
    return <span>{row.syncType}</span>;
  };

  const statusTemplate = (row: IHstSyncRecord) => {
    return <StatusBadge status={row.syncStatus} value={row.syncStatus} />;
  };

  const syncedAtTemplate = (row: IHstSyncRecord) => {
    return <span>{formatDate(row.syncedAt)}</span>;
  };

  const errorTemplate = (row: IHstSyncRecord) => {
    if (!row.errorMessage) return <span>-</span>;

    const tooltipId = `error-tooltip-${row.id}`;

    return (
      <>
        <Tooltip target={`.${tooltipId}`} position="top" />
        <ErrorText className={tooltipId} data-pr-tooltip={row.errorMessage}>
          {row.errorMessage}
        </ErrorText>
      </>
    );
  };

  const handleRowClick = (row: IHstSyncRecord) => {
    if (row.orderId) {
      window.open(`/orders/${row.orderId}`, "_blank");
    }
  };

  const parsedRecords = useMemo(() => {
    return records.map((record) => ({
      ...record,
    }));
  }, [records]);

  if (statsLoading) return <LoadingSpinner />;

  return (
    <TabView className="w-full">
      <TabPanel header="HST Payment Sync">
        <div>
          {/* Stats Cards */}
          <StatsContainer>
            <StatCard clickable onClick={() => handleStatusCardClick(null)}>
              <div className="stat-count">{stats?.total || 0}</div>
              <div className="stat-label">Total</div>
            </StatCard>
            <StatCard
              variant="pending"
              clickable
              onClick={() => handleStatusCardClick("PENDING")}
            >
              <div className="stat-count">{stats?.pending || 0}</div>
              <div className="stat-label">Pending</div>
            </StatCard>
            <StatCard
              variant="inProgress"
              clickable
              onClick={() => handleStatusCardClick("IN_PROGRESS")}
            >
              <div className="stat-count">{stats?.inProgress || 0}</div>
              <div className="stat-label">In Progress</div>
            </StatCard>
            <StatCard
              variant="synced"
              clickable
              onClick={() => handleStatusCardClick("SYNCED")}
            >
              <div className="stat-count">{stats?.synced || 0}</div>
              <div className="stat-label">Synced</div>
            </StatCard>
            <StatCard
              variant="failed"
              clickable
              onClick={() => handleStatusCardClick("FAILED")}
            >
              <div className="stat-count">{stats?.failed || 0}</div>
              <div className="stat-label">Failed</div>
            </StatCard>
            <StatCard
              variant="skipped"
              clickable
              onClick={() => handleStatusCardClick("SKIPPED")}
            >
              <div className="stat-count">{stats?.skipped || 0}</div>
              <div className="stat-label">Skipped</div>
            </StatCard>
          </StatsContainer>

          {/* Filters Bar */}
          <ItemBar>
            <ItemBarLeft>
              <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                  placeholder="Search patient name..."
                  value={patientNameFilter}
                  onChange={(e) => setPatientNameFilter(e.target.value)}
                  style={{ width: "280px" }}
                />
              </span>
              <Dropdown
                value={statusFilter}
                options={statusOptions}
                onChange={(e) => {
                  setStatusFilter(e.value);
                  setFirst(0);
                }}
                placeholder="Status"
                style={{ width: "180px" }}
              />
            </ItemBarLeft>
          </ItemBar>

          {/* Data Table */}
          {recordsLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <StyledDataTable
                value={parsedRecords}
                emptyMessage="No sync records found."
                stripedRows
                responsiveLayout="scroll"
                onRowClick={(e) => handleRowClick(e.data as IHstSyncRecord)}
                rowClassName={() => "cursor-pointer"}
              >
                <Column
                  field="patientName"
                  header="Patient Name"
                  body={patientNameTemplate}
                  headerStyle={{ width: "15%" }}
                />
                <Column
                  field="patientDob"
                  header="DOB"
                  body={dobTemplate}
                  headerStyle={{ width: "10%" }}
                />
                <Column
                  field="accountNumber"
                  header="Account #"
                  headerStyle={{ width: "10%" }}
                />
                <Column
                  field="clearPriceInCents"
                  header="Clear Price"
                  body={clearPriceTemplate}
                  headerStyle={{ width: "10%" }}
                />
                <Column
                  field="amountInCents"
                  header="Payment Amount"
                  body={amountTemplate}
                  headerStyle={{ width: "10%" }}
                />
                <Column
                  field="syncType"
                  header="Type"
                  body={syncTypeTemplate}
                  headerStyle={{ width: "8%" }}
                />
                <Column
                  field="syncStatus"
                  header="Status"
                  body={statusTemplate}
                  headerStyle={{ width: "10%" }}
                />
                <Column
                  field="syncedAt"
                  header="Synced At"
                  body={syncedAtTemplate}
                  headerStyle={{ width: "15%" }}
                />
                <Column
                  field="errorMessage"
                  header="Error"
                  body={errorTemplate}
                  headerStyle={{ width: "18%" }}
                />
              </StyledDataTable>
              <Paginator
                first={first}
                rows={PAGE_SIZE}
                totalRecords={totalRecords}
                onPageChange={onPageChange}
              />
            </>
          )}
        </div>
      </TabPanel>
    </TabView>
  );
}
